import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { Col, Row, Typography } from "antd";
import { PRODUCT_TYPE, SHIPPING_TYPE, STATUS_ORDER } from "config/constants";
import Card from "features/Cart/components/Card";
import { ORDER_PRIORITY } from "features/Cart/constants";
import { renderOrderPriority, renderOrderType } from "features/Order/helper";
import {
  buildCustomerListUrl,
  buildSellerApprovalSearchUrl,
  isWaitingSellerApproval,
} from "features/PotentialCustomer/helper";
import { useCustomerPermissions } from "hooks/potentialCustomer/query";
import { useSellerPermissions } from "hooks/seller";
import { t } from "i18next";
import { compact, join } from "lodash";
import moment from "moment";
import { SELLER_APPROVAL_STATUS } from "pages/App/Agent/constants";
import { Link } from "react-router-dom";
import DeliveryDeadline from "../DeliveryDeadline";
import DeliveryMethod from "../DeliveryMethod";
import PickupInfoSection from "../PickupInfoSection";
import InfoReceiverAndSender from "../InfoReceiverAndSender";
import VatExportInfo from "../VatExportInfo";
import useInfoAgent from "./useInfoAgent";

const InfoAgent = ({ dataInfoAgent, order, handleOpenChangeWarehouse, onRefetch }) => {
  const {
    seller,
    orderType,
    orderPriority,
    exportedWarehouse,
    deliveryDeadline,
    fullStockRequired,
    status,
    minDeliveryDate,
    maxDeliveryDate,
  } = dataInfoAgent;

  const { infoReceiverAndSender } = useInfoAgent({ dataInfoAgent });

  // /potential-customer-list cần CUSTOMERS_VIEW; /seller và /seller/detail/:id cần SELLER_VIEW
  // (route detail trả Page403). Thiếu quyền thì để nguyên text, tránh dẫn người dùng tới màn bị chặn.
  const { canCustomerView } = useCustomerPermissions();
  const { canView: canSellerView } = useSellerPermissions();

  const agentTitle = join(compact([seller?.fullName, seller?.telephone]), " | ");

  // Chưa duyệt thì sang màn danh sách chứa họ (không có route chi tiết khách hàng riêng —
  // chi tiết là Drawer trong màn danh sách). Hai màn danh sách nằm sau hai quyền khác nhau.
  const isApprovedSeller = seller?.approvalStatus === SELLER_APPROVAL_STATUS.APPROVED;
  const canOpenList = isWaitingSellerApproval(order?.potentialCustomer) ? canSellerView : canCustomerView;
  const customerListUrl = !isApprovedSeller && canOpenList && buildCustomerListUrl(order?.potentialCustomer);

  // Không có `potentialCustomer` thì vẫn sang được tab Chờ duyệt, chỉ là lọc bằng tìm kiếm thay vì
  // bằng ID. Hết cách mới về Chi tiết đại lý, lấy ID từ `order.sellerID` vì query chi tiết đơn
  // không select `seller.id`. Cả hai route đều chặn bằng SELLER_VIEW.
  const agentDetailUrl =
    customerListUrl ||
    (canSellerView &&
      ((!isApprovedSeller && buildSellerApprovalSearchUrl(seller)) ||
        (order?.sellerID && `/seller/detail/${order?.sellerID}`)));

  const agentTitleNode = (
    <Typography.Text strong style={{ color: "#052987" }}>
      {agentTitle}
    </Typography.Text>
  );

  const renderOrderPriorityLabel = {
    [`${ORDER_PRIORITY.LOW}`]: (
      <Typography.Text strong style={{ color: "#052987" }}>
        {renderOrderPriority[orderPriority]}
      </Typography.Text>
    ),
    [`${ORDER_PRIORITY.MEDIUM}`]: (
      <Typography.Text strong style={{ color: "#00AB78" }}>
        {renderOrderPriority[orderPriority]}
      </Typography.Text>
    ),
    [`${ORDER_PRIORITY.HIGH}`]: (
      <Typography.Text strong style={{ color: "#D2434D" }}>
        {renderOrderPriority[orderPriority]}
      </Typography.Text>
    ),
  };

  const hasDigitalProduct = order?.items?.some(
    (item) =>
      item?.product?.productType === PRODUCT_TYPE.KEY_LICENSE || item?.product?.productType === PRODUCT_TYPE.ACCOUNT
  );

  return (
    <Card
      title={
        <Typography.Text strong style={{ color: "#052987" }}>
          {t("cart.infoAgent")}
        </Typography.Text>
      }
    >
      <Row gutter={16}>
        <Col span={12} style={{ borderRight: "1px dashed #9CADC4" }}>
          <Row gutter={[16, 8]}>
            <Col span={16}>
              {/* Chỉ tên và số điện thoại là phần bấm được, chú thích trạng thái để ngoài Link.
                  Title rỗng (thiếu cả tên và số) hoặc không có URL thì để text thường, không
                  render link chết. Typography.Text nằm trong Link để giữ màu #052987. */}
              {agentTitle && agentDetailUrl ? (
                <Link to={agentDetailUrl} target="_blank">
                  {agentTitleNode}
                </Link>
              ) : (
                agentTitleNode
              )}
              {seller?.approvalStatus !== SELLER_APPROVAL_STATUS.APPROVED &&
                status === STATUS_ORDER.WAITING_FOR_APPROVAL_ORDER && (
                  <span style={{ fontSize: "13px", color: "#5B6673" }}> (Chờ xác thực)</span>
                )}
            </Col>
            <Col span={8}>
              <Typography.Text style={{ color: "#052987" }}>{renderOrderType[orderType]}</Typography.Text>
            </Col>
            {infoReceiverAndSender?.map((item, index) => (
              <Col key={index} span={24}>
                <InfoReceiverAndSender infoUser={item} />
              </Col>
            ))}
            {hasDigitalProduct && (
              <Col span={24}>
                <Row>
                  <Col span={5}>
                    <Typography.Text strong>Email</Typography.Text>
                  </Col>
                  <Col>
                    <Typography.Text>{order?.email || "--"}</Typography.Text>
                  </Col>
                </Row>
              </Col>
            )}
            <DeliveryMethod dataInfoAgent={dataInfoAgent} order={order} onRefetch={onRefetch} />
            {order?.shippingType === SHIPPING_TYPE.PICK_UP_COUNTER && (
              <Col span={24}>
                <PickupInfoSection order={order} canEdit={false} onRefetch={onRefetch} />
              </Col>
            )}
          </Row>
        </Col>
        <Col span={6} style={{ borderRight: "1px dashed #9CADC4" }}>
          <Row gutter={[16, 8]}>
            <Col span={8}>
              <Typography.Text strong>{t("seller.salesMan")}</Typography.Text>
            </Col>
            <Col span={8}>
              <Typography.Text strong style={{ color: "#052987" }}>
                {order?.salesman?.fullname || "--"}
              </Typography.Text>
            </Col>
            <Col span={8}></Col>
            <Col span={8}>
              <Typography.Text strong>{t("cart.exportWarehouseLabel")}</Typography.Text>
            </Col>
            <Col span={8}>
              <Typography.Text strong style={{ color: "#052987", textAlign: "center" }}>
                {exportedWarehouse?.name}
              </Typography.Text>
            </Col>
            <Col span={8}>
              {[
                STATUS_ORDER.WAITING_FOR_APPROVAL_STOCK,
                STATUS_ORDER.WAITING_FOR_ENOUGH_STOCK,
                STATUS_ORDER.WAITING_FOR_PACKAGING,
                STATUS_ORDER.WAITING_FOR_EXPORTING,
              ].includes(order?.status) && (
                <Typography.Link onClick={handleOpenChangeWarehouse} style={{ color: "#052987" }}>
                  {t("v2.order.detail.linkChangeWarehouse")}
                </Typography.Link>
              )}
            </Col>
            <Col span={8}>
              <Typography.Text strong>{t("cart.orderPriorityLabel")}</Typography.Text>
            </Col>
            <Col span={8}>{renderOrderPriorityLabel[orderPriority]}</Col>
            <Col span={8}></Col>
            <Col span={8}>
              {minDeliveryDate && maxDeliveryDate ? (
                <Typography.Text strong>Dự kiến giao hàng</Typography.Text>
              ) : (
                <Typography.Text strong>Hạn chót</Typography.Text>
              )}
            </Col>
            <Col span={8}>
              <DeliveryDeadline
                deliveryDeadline={deliveryDeadline}
                strong
                expectedDeliveryFrom={minDeliveryDate}
                expectedDeliveryTo={maxDeliveryDate}
                timeCheck={
                  status === "COMPLETED" || status === "CANCELED"
                    ? moment(order?.fullEvents[order?.fullEvents.length - 1].createdAt)
                    : moment()
                }
                statusCheck={status === "COMPLETED" || status === "CANCELED"}
              />
            </Col>
            <Col span={8}></Col>
            <Col span={24}>
              {fullStockRequired ? (
                <CheckCircleFilled style={{ color: "#00AB78" }} />
              ) : (
                <CloseCircleFilled style={{ color: "#D2434D" }} />
              )}
              <Typography.Text strong style={{ marginLeft: "8px" }}>
                {t("cart.fullStockRequiredLabel")}
              </Typography.Text>
            </Col>
          </Row>
        </Col>
        <Col span={6}>
          <VatExportInfo dataInfoAgent={dataInfoAgent} order={order} onRefetch={onRefetch} />
        </Col>
      </Row>
    </Card>
  );
};

export default InfoAgent;
